import { Play } from '@tunasong/icons'
import { QUALITY_POOL } from '@tunasong/music-lib'
import type { ChordQualityKind, QualityQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { EarTrainingAnswerButton } from './ear-training-answer-button.js'
import type { AnswerButtonState } from './ear-training-answer-button.js'
import styles from './quality-question.module.css'

export interface QualityQuestionPanelProps {
  question: QualityQuestion
  scoreCorrect: number
  scoreAnswered: number
  /**
   * When false, answer buttons render dimmed (post-answer feedback phase).
   * The Hear-it button stays interactive in both modes.
   */
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: ChordQualityKind) => void
}

/**
 * Ear Training Chord-quality ID question panel. Renders a generic "What
 * quality did you hear?" prompt + Hear-it audition button + 7-button answer
 * grid (major / minor / dim / aug / dom7 / maj7 / min7).
 *
 * Like the interval panel, the chord name and root pitch are not surfaced —
 * the quality is the answer.
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.3
 */
export const QualityQuestionPanel: FC<QualityQuestionPanelProps> = props => {
  const { scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  const buttonState: AnswerButtonState = interactive ? 'idle' : 'dimmed'
  return (
    <section className={styles.question} aria-label="Ear training question">
      <header className={styles.header}>
        <span
          className={styles.scoreChip}
          aria-label={`${scoreCorrect} correct out of ${scoreAnswered} answered`}
        >
          {scoreCorrect} / {scoreAnswered}
        </span>
      </header>
      <p className={styles.prompt}>What quality did you hear?</p>
      <div className={styles.actionRow}>
        <Button variant="outlined" size="small" startIcon={<Play />} onClick={onHearIt}>
          Hear it
        </Button>
      </div>
      <div className={styles.cardGrid}>
        {QUALITY_POOL.map(entry => (
          <EarTrainingAnswerButton
            key={entry.kind}
            pickValue={entry.kind}
            lexiconId={entry.lexiconId}
            fallbackLongName={entry.displayName}
            fallbackShortName={entry.displayNameShort}
            state={buttonState}
            onPick={picked => onAnswer(picked as ChordQualityKind)}
          />
        ))}
      </div>
    </section>
  )
}
